import React from 'react';
import styled from 'styled-components';
import { Control, Controller, FieldError, ValidationRules } from 'react-hook-form';
import TextComponent from '../TextComponent';
import theme from '@/styles/theme';

export interface FormInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  width?: number;
  keyName: string;
  control: Control;
  defaultValue?: string;
  type?: string;
  rightElement?: React.ReactElement;
  leftElement?: React.ReactElement;
  placeholder?: string;
  height?: number;
  onChange?: any;
  value?: any;
  label?: any;
  rules?: ValidationRules;
  error?: FieldError;
  children?: React.ReactNode;
}

const FormInputComponent = (props: FormInputProps) => {
  const {
    width,
    keyName,
    type,
    control,
    leftElement,
    rightElement,
    placeholder,
    rules,
    error,
    onChange,
    defaultValue,
    children,
    value,
    label,
    ...textInputProps
  } = props;

  return (
    <Input width={width}>
      <Row>
        {label && (
          <Label>
            <LabelText>{label}</LabelText>
          </Label>
        )}
        {leftElement && <Left>{leftElement}</Left>}
        <InputForm>
          <Controller
            as={
              <InputWrapper>
                {String(value) ? (
                  <StyledTextInput
                    value={value}
                    onChange={onChange}
                    type={type}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    autoComplete="new-password"
                    {...textInputProps}
                  />
                ) : (
                  <StyledTextInput
                    type={type}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    autoComplete="new-password"
                    {...textInputProps}
                  />
                )}
                <ChildWrapper>{children}</ChildWrapper>
              </InputWrapper>
            }
            control={control}
            rules={rules}
            name={keyName}
            defaultValue={defaultValue}
          />
        </InputForm>
        {rightElement && <Right>{rightElement}</Right>}
      </Row>
      <Error label={label}>
        <ErrorText>{`${error ? error.message : ' '}`}</ErrorText>
      </Error>
    </Input>
  );
};

export default FormInputComponent;

const Input = styled.div<{ width?: number }>`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  flex-flow: column;
  justify-content: space-around;
`;

const StyledTextInput = styled.input`
  width: 100%;
  border: 1px solid ${theme.DF_COLOR.GRAY100};
  color: ${theme.DF_COLOR.BLACK};
  border-radius: 5px;
  font-size: 18px;
  padding: 1.2rem 1.1rem;

  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:disabled {
    background: ${theme.utils.withOpacity(theme.DF_COLOR.GRAY100, 0.3)};
    border-color: ${theme.utils.withOpacity(theme.DF_COLOR.GRAY100, 0.3)};
    color: ${theme.DF_COLOR.GRAY600};
  }
`;

const Error = styled.div<{ label: string }>`
  ${({ label }) => label && `margin-left: 30%`}
`;
const ErrorText = styled.h3`
  display: flex;
  align-items: center;
  width: 100%;
  height: 25px;
  padding: 0 1.2rem;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.DF_COLOR.PRIMARY500};
`;

const Row = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  flex: 1;
`;

const Label = styled.div`
  width: 30%;
`;

const LabelText = styled(TextComponent)`
  color: #000;
  font-size: 16px;
  font-weight: 600;
`;

const InputForm = styled.div`
  display: flex;
  flex-flow: row;
  flex: 1;
`;

const Left = styled.div`
  padding-right: 10px;
  display: flex;
`;
const Right = styled.div`
  padding-left: 10px;
  display: flex;
`;

const InputWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: center;
`;

const ChildWrapper = styled.div`
  position: absolute;
  color: ${theme.DF_COLOR.GRAY600};
  right: 50px;
`;
